import { useReactiveVar } from '@apollo/client'
import { Button } from '@material-ui/core'
import React, { useState } from 'react'
import { Plus } from 'react-feather'
import { useTranslation } from 'react-i18next'
import {
  dictionariesVar,
  projectVar,
  selectedVersionsVar,
} from '../../apollo/reactive-vars'
import { useCosts } from '../../costs/use-costs'
import {
  CostFragment,
  CostType,
  CreateCostInput,
} from '../../generated/graphql'
import { useIds } from '../../project-versions/use-ids'
import { EMPTY_VALUE } from '../common/constants'
import { Loader } from '../common/Loader'
import { LoadingDialog } from '../common/LoadingDialog'
import { Section } from '../common/Section'
import { CostRowDataForPrefill } from './cost-tables/cost-table-util'
import { CostsTables } from './cost-tables/CostsTables'
import { mapCostToFormValues, mapFormValuesToInput } from './costs-util'
import CostsDialog, { CostsDialogFormValues } from './CostsDialog'

export function CostsPage(): React.ReactElement {
  const { t } = useTranslation()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingCost, setEditingCost] = useState<CostFragment | undefined>()

  const project = useReactiveVar(projectVar)
  const selectedVersions = useReactiveVar(selectedVersionsVar)
  const { costIdsToVersionIds } = useReactiveVar(dictionariesVar)
  const { getProviderId } = useIds()

  const {
    createCost,
    isCreateLoading: isCostCreateLoading,
    updateCost,
    deleteCost,
  } = useCosts()

  const defaultVersion = project?.versions.find((version) => version.isDefault)
  const defaultCostFormValues = {
    projectVersionId: defaultVersion?.id,
    providerId: EMPTY_VALUE,
    costType: CostType.Initial,
  }
  const [defaultValues, setDefaultValues] = useState<
    Partial<CostsDialogFormValues>
  >(defaultCostFormValues)

  if (!project) {
    return <Loader />
  }

  const handleDialogClose = () => {
    setDefaultValues(defaultCostFormValues)
    setIsDialogOpen(false)
    setEditingCost(undefined)
  }

  const onAddCostSubmit = async (values: CostsDialogFormValues) => {
    const { projectVersionId, costInput, providerId } =
      mapFormValuesToInput(values)
    await createCost(projectVersionId, costInput as CreateCostInput, providerId)
    handleDialogClose()
  }

  const onEditCostSubmit = async (values: CostsDialogFormValues) => {
    if (!editingCost) {
      return
    }
    const { costInput } = mapFormValuesToInput(values)
    await updateCost(editingCost.id, costInput)
    handleDialogClose()
  }

  const onDelete = async () => {
    if (!editingCost) {
      return
    }
    const projectVersionId = costIdsToVersionIds[editingCost.id]
    await deleteCost(editingCost.id, projectVersionId)
    handleDialogClose()
  }

  const onEditStart = (costId: string, versionId: string) => {
    const cost = project.versions
      .find((version) => version.id === versionId)
      ?.costs.find((c) => c.id === costId)
    if (cost) {
      setDefaultValues(mapCostToFormValues(cost, versionId))
      setIsDialogOpen(true)
      setEditingCost(cost)
    }
  }

  const onCreatePrefilledCost = (
    rowData: CostRowDataForPrefill,
    newCostVersionId: string,
    groupedCostName: string,
  ) => {
    const providerId = getProviderId(
      newCostVersionId,
      groupedCostName,
      rowData.providerName,
    )
    const defaultVersionCost = project.versions
      .find((version) => version.id === rowData.cost.versionId)
      ?.costs.find((c) => c.id === rowData.cost.id)
    if (defaultVersionCost) {
      setDefaultValues(
        mapCostToFormValues(
          { ...defaultVersionCost, providerId },
          newCostVersionId,
        ),
      )
      setIsDialogOpen(true)
    }
  }

  return (
    <>
      <Section
        title={t('costs.breakdown')}
        addon={
          <Button
            data-cy="add-cost-button"
            id="add-cost"
            variant="contained"
            color="primary"
            startIcon={<Plus />}
            onClick={() => setIsDialogOpen(true)}
          >
            {t('costs.dialog.title')}
          </Button>
        }
      >
        <CostsTables
          updateCost={onEditStart}
          createNewCost={onCreatePrefilledCost}
          selectedVersions={selectedVersions}
        />
      </Section>

      <CostsDialog
        project={project}
        isOpen={isDialogOpen}
        onClose={handleDialogClose}
        onSubmit={editingCost ? onEditCostSubmit : onAddCostSubmit}
        onDelete={onDelete}
        defaultValues={defaultValues}
        costId={editingCost?.id}
      />

      <LoadingDialog isLoading={isCostCreateLoading} />
    </>
  )
}
